import posthog from "posthog-js";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { actionTypes } from "./leaveApprovalActionTypes";
import { Common } from "../../../utils";

const { LeaveApproval, TimeOff } = config.REST_API;

// Action Creators
export const setLeaveApprovals = (approvals) => ({
    type: actionTypes.SET_LEAVE_APPROVALS,
    payload: approvals,
});

export const setPendingApprovals = (approvals) => ({
    type: actionTypes.SET_PENDING_APPROVALS,
    payload: approvals,
});

export const setApprovalHistory = (history) => ({
    type: actionTypes.SET_APPROVAL_HISTORY,
    payload: history,
});

export const setApprovalStats = (stats) => ({
    type: actionTypes.SET_APPROVAL_STATS,
    payload: stats,
});

export const setLoading = (loading) => ({
    type: actionTypes.SET_LOADING,
    payload: loading,
});

export const setError = (error) => ({
    type: actionTypes.SET_ERROR,
    payload: error,
});

export const clearApprovals = () => ({
    type: actionTypes.CLEAR_APPROVALS,
});

// API Actions
export const getApprovalStats = () => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = {};
        dispatch(setLoading(true));
        return axiosBasic.get(LeaveApproval.GetApprovalStats).then((response) => {
            const success = response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getApprovalStats", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : response.data.message,
                });
            }
            if (success) {
                dispatch(setApprovalStats(response.data.data));
            } else {
                dispatch(setError(response.data.message));
                Common.showErrorMessage(response.data.message);
            }
            dispatch(setLoading(false));
        }).catch((error) => {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getApprovalStats", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch(setLoading(false));
            Common.showErrorMessage(error);
        });
    };
};

export const getLeaveRequest = (status = null, page = 1, take = 12, append = false) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { status, page, take, append };
        if (append) {
            dispatch(setLoadingMore(true));
        } else {
            dispatch(setLoading(true));
        }

        const url = status !== null
            ? `${LeaveApproval.GetLeaveRequest}?status=${status}&page=${page}&take=${take}`
            : `${LeaveApproval.GetLeaveRequest}?page=${page}&take=${take}`;

        return axiosBasic.get(url).then((response) => {
            const success = response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getLeaveRequest", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : response.data.message,
                });
            }
            if (success) {
                const responseData = response.data.data;
                const paginationInfo = {
                    currentPage: responseData.pagination.currentPage,
                    pageSize: responseData.pagination.take,
                    totalPages: responseData.pagination.totalPages,
                    totalItems: responseData.pagination.totalCount,
                    hasMore: responseData.pagination.hasNextPage,
                };

                if (append) {
                    dispatch(appendLeaveRequest({ data: responseData.data, pagination: paginationInfo }));
                } else {
                    dispatch(setLeaveRequest({ data: responseData.data, pagination: paginationInfo }));
                }
            } else {
                dispatch(setError(response.data.message));
                Common.showErrorMessage(response.data.message);
            }
            if (append) {
                dispatch(setLoadingMore(false));
            } else {
                dispatch(setLoading(false));
            }
        }).catch((error) => {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getLeaveRequest", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch(setError(error.message));
            if (append) {
                dispatch(setLoadingMore(false));
            } else {
                dispatch(setLoading(false));
            }
            Common.showErrorMessage(error);
        });
    };
};

export const searchLeaveApprovals = (searchParams) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = {
            page: searchParams?.page,
            take: searchParams?.take,
            approvalStatus: searchParams?.approvalStatus,
            sortBy: searchParams?.sortBy,
            sortDirection: searchParams?.sortDirection,
            hasQuery: !!(searchParams?.query && String(searchParams.query).trim()),
            hasDateRange: !!(searchParams?.start && searchParams?.end),
        };
        dispatch(setLoading(true));

        // Build query parameters
        const params = new URLSearchParams();

        if (searchParams.query) params.append('query', searchParams.query);
        if (searchParams.start) params.append('start', searchParams.start);
        if (searchParams.end) params.append('end', searchParams.end);
        if (searchParams.approvalStatus !== null && searchParams.approvalStatus !== undefined) {
            params.append('approvalStatus', searchParams.approvalStatus);
        }
        if (searchParams.leaveTypeName) params.append('leaveTypeName', searchParams.leaveTypeName);
        if (searchParams.departmentName) params.append('departmentName', searchParams.departmentName);
        if (searchParams.officeName) params.append('officeName', searchParams.officeName);
        if (searchParams.approverName) params.append('approverName', searchParams.approverName);
        if (searchParams.isActive !== undefined) params.append('isActive', searchParams.isActive);
        if (searchParams.page) params.append('page', searchParams.page);
        if (searchParams.take) params.append('take', searchParams.take);
        if (searchParams.sortBy) params.append('sortBy', searchParams.sortBy);
        if (searchParams.sortDirection) params.append('sortDirection', searchParams.sortDirection);

        const url = `${LeaveApproval.SearchLeaveApprovals}?${params.toString()}`;

        return axiosBasic.get(url).then((response) => {
            const success = response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("searchLeaveApprovals", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : response.data.message,
                });
            }
            if (success) {
                const responseData = response.data.data;
                const paginationInfo = {
                    currentPage: responseData.pagination?.currentPage || 1,
                    pageSize: responseData.pagination?.take || 12,
                    totalPages: responseData.pagination?.totalPages || 1,
                    totalItems: responseData.pagination?.totalCount || 0,
                    hasMore: responseData.pagination?.hasNextPage || false,
                };

                dispatch(setLeaveRequest({ data: responseData.data || responseData, pagination: paginationInfo }));
            } else {
                dispatch(setError(response.data.message));
                Common.showErrorMessage(response.data.message);
            }
            dispatch(setLoading(false));
        }).catch((error) => {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("searchLeaveApprovals", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch(setError(error.message));
            dispatch(setLoading(false));
            Common.showErrorMessage(error.message);
        });
    };
};

export const setLeaveRequest = (cards) => ({
    type: actionTypes.SET_LEAVE_REQUEST_CARDS,
    payload: cards,
});

export const appendLeaveRequest = (cards) => ({
    type: actionTypes.APPEND_LEAVE_REQUEST_CARDS,
    payload: cards,
});

export const setPaginationInfo = (pagination) => ({
    type: actionTypes.SET_PAGINATION_INFO,
    payload: pagination,
});

export const setLoadingMore = (loading) => ({
    type: actionTypes.SET_LOADING_MORE,
    payload: loading,
});

export const getAllApprovalsWithUserDetails = () => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = {};
        dispatch(setLoading(true));
        return axiosBasic
            .get(LeaveApproval.GetAllWithUserDetails)
            .then((response) => {
                const success = response.data.success;
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getAllApprovalsWithUserDetails", {
                        type: success ? "info" : "exception",
                        userId,
                        success,
                        userInput,
                        reason: success ? undefined : response.data.message,
                    });
                }
                if (success) {
                    dispatch(setLeaveApprovals(response.data.data));
                } else {
                    dispatch(setError(response.data.message));
                    Common.showErrorMessage(response.data.message);
                }
                dispatch(setLoading(false));
            })
            .catch((error) => {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getAllApprovalsWithUserDetails", {
                        type: "exception",
                        userId,
                        success: false,
                        userInput,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    });
                }
                dispatch(setError(error.message));
                dispatch(setLoading(false));
                Common.showErrorMessage(error);
            });
    };
};



export const getPendingApprovalsForUser = (targetUserId) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { targetUserId };
        dispatch(setLoading(true));
        return axiosBasic
            .get(LeaveApproval.GetPendingForUser + targetUserId)
            .then((response) => {
                const success = response.data.success;
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getPendingApprovalsForUser", {
                        type: success ? "info" : "exception",
                        userId,
                        success,
                        userInput,
                        reason: success ? undefined : response.data.message,
                    });
                }
                if (success) {
                    dispatch(setPendingApprovals(response.data.data));
                } else {
                    dispatch(setError(response.data.message));
                    Common.showErrorMessage(response.data.message);
                }
                dispatch(setLoading(false));
            })
            .catch((error) => {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getPendingApprovalsForUser", {
                        type: "exception",
                        userId,
                        success: false,
                        userInput,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    });
                }
                dispatch(setError(error.message));
                dispatch(setLoading(false));
                Common.showErrorMessage(error);
            });
    };
};

export const updateLeaveStatus = (approvalData, currentFilterStatus = "pending", options = {}) => {
    const { refreshApprovalList = true } = options; // When false (e.g. cancel from time-off modal), skip GetLeaveRequest
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { currentFilterStatus, groupId: approvalData?.groupId, status: approvalData?.status };
        dispatch(setLoading(true));
        return axiosBasic
            .put(LeaveApproval.UpdateLeaveStatus, approvalData)
            .then((response) => {
                const success = response.data.success;
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("updateLeaveStatus", {
                        type: success ? "info" : "exception",
                        userId,
                        success,
                        userInput,
                        reason: success ? undefined : response.data.message,
                    });
                }
                if (success) {
                    Common.showSuccessMessage(response.data.message || "Leave approval updated successfully");

                    const responseData = response.data.data;
                    if (responseData && refreshApprovalList) {
                        // Only refresh Leave Approvals list when action was from the approval screen.
                        // Map tab name or filterStatus string ("0"/"1"/"2") to API status (0=pending, 1=approved, 2=rejected, null=all)
                        const statusValue =
                            currentFilterStatus === "pending" || currentFilterStatus === "0" ? 0 :
                                currentFilterStatus === "approved" || currentFilterStatus === "1" ? 1 :
                                    currentFilterStatus === "rejected" || currentFilterStatus === "2" ? 2 : null;
                        dispatch(getLeaveRequest(statusValue, 1, 12, false));
                    }

                    if (responseData && refreshApprovalList) {
                        if (responseData.approvalStats) {
                            dispatch(setApprovalStats(responseData.approvalStats));
                        } else {
                            dispatch(getApprovalStats());
                        }
                    }
                } else {
                    dispatch(setError(response.data.message));
                    Common.showErrorMessage(response.data.message);
                }
                dispatch(setLoading(false));
                return response.data;
            })
            .catch((error) => {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("updateLeaveStatus", {
                        type: "exception",
                        userId,
                        success: false,
                        userInput,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    });
                }
                dispatch(setError(error.message));
                dispatch(setLoading(false));
                Common.showErrorMessage(error);
            });
    };
};

export const createLeaveApproval = (approvalData) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { hasData: !!approvalData, groupId: approvalData?.groupId };
        dispatch(setLoading(true));
        return axiosBasic
            .post(LeaveApproval.Create, approvalData)
            .then((response) => {
                const success = response.data.success;
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("createLeaveApproval", {
                        type: success ? "info" : "exception",
                        userId,
                        success,
                        userInput,
                        reason: success ? undefined : response.data.message,
                    });
                }
                if (success) {
                    Common.showSuccessMessage(response.data.message || "Leave approval created successfully");
                } else {
                    dispatch(setError(response.data.message));
                    Common.showErrorMessage(response.data.message);
                }
                dispatch(setLoading(false));
                return response.data;
            })
            .catch((error) => {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("createLeaveApproval", {
                        type: "exception",
                        userId,
                        success: false,
                        userInput,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    });
                }
                dispatch(setError(error.message));
                dispatch(setLoading(false));
                Common.showErrorMessage(error);
                throw error;
            });
    };
};

export const getApprovalHistoryByLeaveId = (leaveId) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { leaveId };
        dispatch(setLoading(true));
        return axiosBasic
            .get(LeaveApproval.GetHistoryByLeaveId + leaveId)
            .then((response) => {
                const success = response.data.success;
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getApprovalHistoryByLeaveId", {
                        type: success ? "info" : "exception",
                        userId,
                        success,
                        userInput,
                        reason: success ? undefined : response.data.message,
                    });
                }
                if (success) {
                    dispatch(setApprovalHistory(response.data.data));
                } else {
                    dispatch(setError(response.data.message));
                    Common.showErrorMessage(response.data.message);
                }
                dispatch(setLoading(false));
            })
            .catch((error) => {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getApprovalHistoryByLeaveId", {
                        type: "exception",
                        userId,
                        success: false,
                        userInput,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    });
                }
                dispatch(setError(error.message));
                dispatch(setLoading(false));
                Common.showErrorMessage(error);
            });
    };
};

export const getApprovalHistoryByGroup = (group) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { group };
        dispatch(setLoading(true));
        return axiosBasic
            .get(LeaveApproval.GetHistoryByGroup + group)
            .then((response) => {
                const success = response.data.success;
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getApprovalHistoryByGroup", {
                        type: success ? "info" : "exception",
                        userId,
                        success,
                        userInput,
                        reason: success ? undefined : response.data.message,
                    });
                }
                if (success) {
                    dispatch(setApprovalHistory(response.data.data));
                } else {
                    dispatch(setError(response.data.message));
                    Common.showErrorMessage(response.data.message);
                }
                dispatch(setLoading(false));
            })
            .catch((error) => {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getApprovalHistoryByGroup", {
                        type: "exception",
                        userId,
                        success: false,
                        userInput,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    });
                }
                dispatch(setError(error.message));
                dispatch(setLoading(false));
                Common.showErrorMessage(error);
            });
    };
};

export const getApprovalHistoryByApprover = (approverId) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { approverId };
        dispatch(setLoading(true));
        return axiosBasic
            .get(LeaveApproval.GetHistoryByApprover + approverId)
            .then((response) => {
                const success = response.data.success;
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getApprovalHistoryByApprover", {
                        type: success ? "info" : "exception",
                        userId,
                        success,
                        userInput,
                        reason: success ? undefined : response.data.message,
                    });
                }
                if (success) {
                    dispatch(setApprovalHistory(response.data.data));
                } else {
                    dispatch(setError(response.data.message));
                    Common.showErrorMessage(response.data.message);
                }
                dispatch(setLoading(false));
            })
            .catch((error) => {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("getApprovalHistoryByApprover", {
                        type: "exception",
                        userId,
                        success: false,
                        userInput,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    });
                }
                dispatch(setError(error.message));
                dispatch(setLoading(false));
                Common.showErrorMessage(error);
            });
    };
};

// Self-requested leaves actions
export const getSelfRequestedLeavesStats = () => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = {};
        dispatch(setSelfLeavesLoading(true));
        return axiosBasic.get(TimeOff.GetSelfRequestedLeavesStats).then((response) => {
            const success = response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getSelfRequestedLeavesStats", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : response.data.message,
                });
            }
            if (success) {
                dispatch(setSelfLeavesStats(response.data.data));
            } else {
                dispatch(setError(response.data.message));
                Common.showErrorMessage(response.data.message);
            }
            dispatch(setSelfLeavesLoading(false));
        }).catch((error) => {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getSelfRequestedLeavesStats", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch(setError(error.message));
            dispatch(setSelfLeavesLoading(false));
            Common.showErrorMessage(error);
        });
    };
};

export const getSelfRequestedLeaves = (status = null, page = 1, take = 10, append = false) => {
    return (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { status, page, take, append };
        if (append) {
            dispatch(setSelfLeavesLoadingMore(true));
        } else {
            dispatch(setSelfLeavesLoading(true));
        }

        const url = status !== null
            ? `${TimeOff.GetSelfRequestedLeaves}?status=${status}&page=${page}&take=${take}`
            : `${TimeOff.GetSelfRequestedLeaves}?page=${page}&take=${take}`;

        return axiosBasic.get(url).then((response) => {
            const success = response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getSelfRequestedLeaves", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : response.data.message,
                });
            }
            if (success) {
                const responseData = response.data.data;

                // Handle null or undefined responseData
                if (!responseData) {
                    const emptyPaginationInfo = {
                        currentPage: page,
                        pageSize: take,
                        totalPages: 0,
                        totalItems: 0,
                        hasMore: false,
                    };
                    if (append) {
                        dispatch(appendSelfLeaves({ data: [], pagination: emptyPaginationInfo }));
                    } else {
                        dispatch(setSelfLeaves({ data: [], pagination: emptyPaginationInfo }));
                    }
                    if (append) {
                        dispatch(setSelfLeavesLoadingMore(false));
                    } else {
                        dispatch(setSelfLeavesLoading(false));
                    }
                    return;
                }

                const paginationInfo = {
                    currentPage: responseData.pagination?.currentPage || page,
                    pageSize: responseData.pagination?.take || take,
                    totalPages: responseData.pagination?.totalPages || 1,
                    totalItems: responseData.pagination?.totalCount || 0,
                    hasMore: responseData.pagination?.hasNextPage || false,
                };

                const leavesData = responseData.data || responseData;
                const leavesArray = Array.isArray(leavesData) ? leavesData : [];

                if (append) {
                    dispatch(appendSelfLeaves({ data: leavesArray, pagination: paginationInfo }));
                } else {
                    dispatch(setSelfLeaves({ data: leavesArray, pagination: paginationInfo }));
                }
            } else {
                dispatch(setError(response.data.message));
                Common.showErrorMessage(response.data.message);
            }
            if (append) {
                dispatch(setSelfLeavesLoadingMore(false));
            } else {
                dispatch(setSelfLeavesLoading(false));
            }
        }).catch((error) => {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getSelfRequestedLeaves", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch(setError(error.message));
            if (append) {
                dispatch(setSelfLeavesLoadingMore(false));
            } else {
                dispatch(setSelfLeavesLoading(false));
            }
            Common.showErrorMessage(error);
        });
    };
};

export const setSelfLeavesStats = (stats) => ({
    type: actionTypes.SET_SELF_LEAVES_STATS,
    payload: stats,
});

export const setSelfLeaves = (leaves) => ({
    type: actionTypes.SET_SELF_LEAVES,
    payload: leaves,
});

export const appendSelfLeaves = (leaves) => ({
    type: actionTypes.APPEND_SELF_LEAVES,
    payload: leaves,
});

export const setSelfLeavesLoading = (loading) => ({
    type: actionTypes.SET_SELF_LEAVES_LOADING,
    payload: loading,
});

export const setSelfLeavesLoadingMore = (loading) => ({
    type: actionTypes.SET_SELF_LEAVES_LOADING_MORE,
    payload: loading,
}); 